import React, { useState, useCallback, useMemo } from 'react';
import { useI18n } from '../i18n';
import { getMemberHex, getCategoryName, sortMembrosBySeniority, formatarMinutos } from '../utils';
import { Card, Segmented } from './ui';

// Top 10 categories (default) or tasks by weighted minutes, as horizontal bars
// stacked by member.
export default function TopStackedChart({ registros, tarefas, categorias, membros }) {
  const { t } = useI18n();
  const [view, setView] = useState('categorias');
  const [tooltip, setTooltip] = useState(null);

  const tarefaById = useMemo(() => new Map(tarefas.map(tf => [tf.id, tf])), [tarefas]);
  const categoriaById = useMemo(() => new Map(categorias.map(c => [c.id, c])), [categorias]);
  const orderedMembros = useMemo(() => sortMembrosBySeniority(membros), [membros]);

  const buildRanking = useCallback((keyFn, labelFn) => {
    const grupos = new Map();
    registros.forEach(reg => {
      const tarefa = tarefaById.get(reg.tarefa_id);
      if (!tarefa) return;
      const key = keyFn(tarefa);
      if (key === null || key === undefined) return;
      const valor = (reg.tempo_minutos || 0) * (tarefa.peso || 0);
      if (valor <= 0) return;
      if (!grupos.has(key)) grupos.set(key, { nome: labelFn(tarefa, key), total: 0, byMembro: {} });
      const entry = grupos.get(key);
      entry.total += valor;
      entry.byMembro[reg.membro_id] = (entry.byMembro[reg.membro_id] || 0) + valor;
    });
    return [...grupos.values()].sort((a, b) => b.total - a.total).slice(0, 10);
  }, [registros, tarefaById]);

  const topTarefas = useMemo(() => buildRanking(tf => tf.id, tf => tf.nome), [buildRanking]);
  const topCategorias = useMemo(
    () => buildRanking(tf => tf.categoria_id, (tf, key) => getCategoryName(categoriaById.get(key), t)),
    [buildRanking, categoriaById, t]
  );

  const dados = view === 'categorias' ? topCategorias : topTarefas;
  const maxTotal = dados.length ? dados[0].total : 0;
  const membrosPresentes = orderedMembros.filter(m => dados.some(g => g.byMembro[m.id] > 0));

  return (
    <Card className="p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{view === 'categorias' ? t('dashboard.topCategoriesTitle') : t('dashboard.topTasksTitle')}</h2>
          <p className="text-sm text-gray-500">{view === 'categorias' ? t('dashboard.topCategoriesSubtitle') : t('dashboard.topTasksSubtitle')}</p>
        </div>
        <Segmented
          value={view}
          onChange={setView}
          options={[['categorias', t('dashboard.tabCategories')], ['tarefas', t('dashboard.tabTasks')]]}
          className="self-start"
        />
      </div>

      {dados.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">{t('dashboard.topTasksEmpty')}</p>
      ) : (
        <>
          {membrosPresentes.length > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-2 mb-4">
              {membrosPresentes.map(m => (
                <div key={m.id} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: getMemberHex(m.id, membros) }} />
                  <span className="text-xs text-gray-600">{m.nome}</span>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2.5">
            {dados.map((g, i) => {
              const segments = orderedMembros
                .filter(m => g.byMembro[m.id] > 0)
                .map(m => ({ id: m.id, nome: m.nome, valor: g.byMembro[m.id] }));
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-24 sm:w-36 text-xs text-gray-700 truncate flex-shrink-0" title={g.nome}>
                    {g.nome}
                  </div>
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                      <div className="h-full flex" style={{ width: `${maxTotal ? (g.total / maxTotal) * 100 : 0}%` }}>
                        {segments.map((seg, si) => {
                          const hex = getMemberHex(seg.id, membros);
                          const showTip = (e) => setTooltip({
                            x: e.clientX,
                            y: e.clientY,
                            grupo: g.nome,
                            membro: seg.nome,
                            hex,
                            valor: formatarMinutos(seg.valor),
                            pct: Math.round((seg.valor / g.total) * 100),
                          });
                          return (
                            <div
                              key={seg.id}
                              className="h-full"
                              style={{
                                width: `${(seg.valor / g.total) * 100}%`,
                                backgroundColor: hex,
                                boxShadow: si < segments.length - 1 ? 'inset -1px 0 0 #ffffff' : 'none',
                              }}
                              onMouseEnter={showTip}
                              onMouseMove={showTip}
                              onMouseLeave={() => setTooltip(null)}
                            />
                          );
                        })}
                      </div>
                    </div>
                    <div className="text-xs font-medium text-gray-700 tabular-nums w-14 text-right flex-shrink-0">
                      {formatarMinutos(g.total)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {tooltip && (
        <div
          className="fixed z-[60] pointer-events-none bg-gray-900 text-white rounded-lg shadow-pop px-3 py-2 text-xs"
          style={{ left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, calc(-100% - 12px))' }}
        >
          <div className="font-semibold mb-1 whitespace-nowrap">{tooltip.grupo}</div>
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: tooltip.hex }} />
            <span>{tooltip.membro}</span>
            <span className="text-gray-300">{tooltip.valor} · {tooltip.pct}%</span>
          </div>
        </div>
      )}
    </Card>
  );
}
